import { useInfiniteQuery } from '@tanstack/react-query';
import { Activity, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ActionLogRow } from './ActionLogRow';
import { UsageStats } from './UsageStats';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { useActionCalls } from '@/Domain/hooks/Action/useActionCalls.hook';

type Props = {
  actionId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actionName?: string;
};

export const ActionLogsSheet = ({ actionId, open, onOpenChange, actionName }: Props) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'logs' });
  const [onlyIssues, setOnlyIssues] = useState(false);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useActionCalls(actionId, onlyIssues, open),
  );

  const calls = data?.pages.flatMap((page) => page.calls) ?? [];

  const onLoadMore = useCallback(() => {
    if (isFetchingNextPage) return;
    fetchNextPage().catch(() => null);
  }, [isFetchingNextPage, fetchNextPage]);

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          onOpenChange(false);
        }}
        className="w-full gap-0 space-y-0 sm:max-w-2xl"
        data-testid="action-logs-sheet"
      >
        <SheetHeader className="pb-4">
          <SheetTitle>{actionName || t('title')}</SheetTitle>
          <IconButton variant="secondary" type="button" onClick={() => onOpenChange(false)}>
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <div ref={rootRef} className="flex-1 min-h-0 space-y-5 overflow-y-auto pt-4 custom-scroll">
          <UsageStats actionId={actionId} />

          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-foreground">{t('recent_calls')}</h3>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Switch
                checked={onlyIssues}
                onCheckedChange={setOnlyIssues}
                data-testid="action-logs-only-issues"
              />
              {t('only_issues')}
            </label>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12" data-testid="action-logs-loading">
              <Spinner className="size-6 text-foreground-subtle" />
            </div>
          ) : calls.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border py-12"
              data-testid="action-logs-empty"
            >
              <Activity className="size-10 text-foreground-subtle" />
              <p className="text-sm font-medium text-foreground-subtle">{t('empty_title')}</p>
              <p className="max-w-xs text-center text-xs text-foreground-subtle">
                {onlyIssues ? t('empty_issues_description') : t('empty_description')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map((call) => (
                <ActionLogRow key={call.id} call={call} />
              ))}
              {hasNextPage && (
                <div
                  ref={sentryRef}
                  className="flex justify-center py-4"
                  data-testid="action-logs-sentinel"
                >
                  <Spinner className="size-5 text-foreground-subtle" />
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
